import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../app/hooks";
import { PageWrapper, PageWrapperProps } from "../../common-ui/PageWrapper";
import { useUsersPendingAssessmentEvents } from "../AssessmentEvents/assessmentEventUtils";
import { selectCurrentUser } from "../Users/usersSlice";
import { Box, Chip, CircularProgress, Typography } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { useTranslation } from "react-i18next";
import { AssessmentCard } from "./AssessmentCard";
import { APIError } from "../../common-ui/APIError";
import { useEffect, useMemo } from "react";
import { Mixpanel } from "../Analytics/Mixpanel";
import { MixpanelEvents } from "../../app/constants";
import { FlattenedAssessmentDetails } from "../AssessmentEvents/assessmentEventsAPI";
import { act } from "react-dom/test-utils";

const pageDetails: PageWrapperProps = {
  className: "PendingAssessments"
};

export const PendingAssessments = () => {
  const currentUser = useAppSelector(selectCurrentUser);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { pendingAssessmentEvents, status, error, getPendingAssessmentEvents } =
    useUsersPendingAssessmentEvents(currentUser?.id);

  const navigateToAssessmentCompletion = (assessmentEventId: string) => {
    navigate(`/assessments/complete/${assessmentEventId}`);
  };

  const flattenedAssessments: FlattenedAssessmentDetails[] = useMemo(() => {
    return (
      pendingAssessmentEvents?.flatMap((group) =>
        group.assessments.map((assessment) => ({
          assessmentEventId: assessment.assessmentEventId,
          assessmentEventSubjectUserId: assessment.assessmentEventSubjectUserId,
          activityTitle: assessment.activityTitle,
          assessmentEventCreationDate: assessment.assessmentCreationDate,
          assessmentEventExpirationDate:
            assessment.assessmentEventExpirationDate,
          sponsorBranding: assessment.sponsor,
          activityEventId: group.activityEventId,
          activityEventDate: group.activityEventDate,
          activityEventNotes: group.activityEventNotes,
          user: group.user
        }))
      ) ?? []
    );
  }, [pendingAssessmentEvents]);

  const pendingAssessmentsCount = flattenedAssessments?.length;

  const headerContent = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Typography fontSize="1.5rem">{t("Assessments to Complete")}</Typography>
      <Chip label={pendingAssessmentsCount} size="small" />
    </Box>
  );

  useEffect(() => {
    Mixpanel.track(MixpanelEvents.HomePageViewed);
  }, []);

  if (status === "loading") {
    return <CircularProgress />;
  }

  if (status === "failed")
    return <APIError error={error ?? "Error loading pending assessments."} />;

  if (status === "succeeded") {
    return (
      <PageWrapper {...pageDetails} headerContent={headerContent}>
        <Grid container xs={12} md={8} spacing={2}>
          {flattenedAssessments.length === 0 && (
            <Grid xs={12}>
              <Typography>{t("You have no pending assessments.")}</Typography>
            </Grid>
          )}
          {flattenedAssessments?.map((assessmentEvent) => (
            <Grid xs={12} key={assessmentEvent.assessmentEventId}>
              <AssessmentCard
                clickable
                assessmentDetails={assessmentEvent}
                onClick={navigateToAssessmentCompletion}
                selfAssessment={
                  assessmentEvent.assessmentEventSubjectUserId === currentUser?.id
                }
                declinable
                refetchPendingAssessments={() => {
                  if (currentUser?.id) {
                    getPendingAssessmentEvents(currentUser.id);
                  }
                }}
              />
            </Grid>
          ))}
        </Grid>
      </PageWrapper>
    );
  }

  return null;
};
